import type { FC } from 'react'
import styled from '@emotion/styled'
import sanitizeHtml from 'sanitize-html'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

interface TwilioSegmentDifferencesProps {
  heading: string
  features: {
    heading: string
    copy: string[]
  }[]
}

const TwilioSegmentDifferences: FC<TwilioSegmentDifferencesProps> = ({ heading, features }) => {
  return (
    <StyledSectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <StyledHeading tag="h2">{heading}</StyledHeading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <StyledColumnTrack columns={1} overrides={{ lg: 2, xl: features.length }}>
          {features.map(({ heading, copy }) => (
            <StyledFeatureBoxBase alignment="left" key={heading}>
              <FeatureBox.Body>
                <StyledFeatureBoxTitle variant="h4" tag="h3">
                  {heading}
                </StyledFeatureBoxTitle>
                <Ul>
                  {copy.map((text) => (
                    <StyledListItem key={text}>
                      <FeatureBox.Text
                        tag="p"
                        variant="pMedium"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(text, { allowedTags: [`a`] }),
                        }}
                      />
                    </StyledListItem>
                  ))}
                </Ul>
              </FeatureBox.Body>
            </StyledFeatureBoxBase>
          ))}
        </StyledColumnTrack>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    row-gap: 40px;
    padding-top: 40px;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      row-gap: 80px;
      padding-top: 0px;
      padding-bottom: 80px;
    }
  }
`

const StyledHeading = styled(CopyText.Heading)`
  text-align: start;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    text-align: center;
    max-width: 644px;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  row-gap: 40px;
  align-items: start;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    column-gap: 80px;
    row-gap: 56px;
  }
`

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  max-width: 520px;
`

const StyledFeatureBoxTitle = styled(FeatureBox.Title)`
  padding-bottom: 8px;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-bottom: 16px;
  }
`

const Ul = styled.ul`
  padding-left: 25px;
  gap: 8px;
  display: grid;
`

const StyledListItem = styled.li`
  list-style-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='9' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.045.667a.91.91 0 0 1 .025 1.305L5.031 8.126a.95.95 0 0 1-1.356 0L.93 5.33a.91.91 0 0 1 .025-1.305.954.954 0 0 1 1.331.025l2.067 2.106L9.714.692a.954.954 0 0 1 1.33-.025Z' fill='%23121C2D'/%3e%3c/svg%3e ");
  font-weight: 400;
  padding-left: 8px;
`

export default TwilioSegmentDifferences
